import { useTheme } from "next-themes"
import { useLocation } from "react-router-dom"
import { Toaster as Sonner } from "sonner"

type ToasterProps = React.ComponentProps<typeof Sonner>

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  // BLU-631: Production stations (`/production/*`) render a full-width sticky
  // action bar pinned to the bottom of the viewport (Accept / Redo / Pack). The
  // default bottom-right sonner position drops success toasts directly over
  // those primary controls and — because a toast captures pointer events — the
  // Accept click silently times out. Push toasts to top-center on production-app
  // routes so they can never cover a station's primary action (matches the
  // top-of-screen, modal-level feedback language in PRODUCTION_DESIGN.md).
  // Inventory-app routes keep the canonical bottom-right placement (DESIGN.md
  // §15.5). The single global Sonner is mounted inside <BrowserRouter> (App.tsx),
  // so useLocation() is always in a router context here.
  const { pathname } = useLocation()
  const isProductionSurface =
    pathname === "/production" || pathname.startsWith("/production/")
  const position: ToasterProps["position"] = isProductionSurface
    ? "top-center"
    : "bottom-right"

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position={position}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
